import {
  BrowserConnectionInfo,
  TelemetryApi,
  TelemetryCapabilities,
  TelemetryHistogramSummary,
  TelemetryRow,
  TelemetrySnapshot,
  TelemetryStatus,
  TelemetryTags,
  TranscriptionMetricsProvider,
  TranscriptionMetricsSnapshot,
} from "./types";

const DEFAULT_THROTTLE_MS = 250;
const DEFAULT_ROW_LIMIT = 2000;
const DEFAULT_AUDIO_METRICS = {
  speechRmsThreshold: 0.015,
  utteranceEndSilenceMs: 800,
  clippingThreshold: 0.99,
  callbackGapThresholdMs: 100,
};
const HISTOGRAM_SAMPLE_SIZE = 256;

export type ResilienceStatusLike = {
  connectionState:
    | "connecting"
    | "connected"
    | "reconnecting"
    | "disconnected"
    | "stopped";
  websocketState: "connecting" | "open" | "closing" | "closed" | "unavailable";
  browserOnline: boolean;
  isRecovering: boolean;
  isBufferingAudio: boolean;
  isDrainingAudio: boolean;
  transportBufferedAmountBytes: number;
  pendingBufferedAudioBytes: number;
  persistedBufferedAudioBytes: number;
  persistedBufferedAudioSegments: number;
  totalBufferedAudioBytes: number;
  bufferingStartedAt: number | null;
  drainStartedAt: number | null;
  lastDrainProgressAt: number | null;
  drainCycles: number;
  drainExitReason:
    | "completed"
    | "socket_unavailable"
    | "send_failed"
    | "stalled"
    | "stopped"
    | null;
  reconnectAttempt: number | null;
  nextReconnectDelayMs: number | null;
  finishDeliveryState: "sent" | "skipped_socket_closed" | "timed_out" | null;
  maxReconnectAttempts: number;
  remainingReconnectAttempts: number | null;
  lastDisconnect: {
    code?: number;
    reason?: string;
    wasClean?: boolean;
    errorMessage?: string;
  } | null;
};

type MetricsEmitCallback = (snapshot: TelemetrySnapshot) => void;
type TelemetryRowCallback = (row: TelemetryRow) => void;

type HistogramState = {
  count: number;
  sum: number;
  min: number | null;
  max: number | null;
  sample: number[];
  sampleCursor: number;
};

const createSessionId = (): string =>
  ["telemetry", Date.now().toString(36), Math.random().toString(36).slice(2, 10)].join("-");

const quantileFromSorted = (sorted: number[], fraction: number): number | null => {
  if (sorted.length === 0) {
    return null;
  }

  const clamped = Math.min(Math.max(fraction, 0), 1);
  const index = Math.ceil(clamped * sorted.length) - 1;
  return sorted[Math.min(Math.max(index, 0), sorted.length - 1)] ?? null;
};

const summarizeHistogram = (state: HistogramState): TelemetryHistogramSummary => {
  const sorted = state.sample.slice().sort((left, right) => left - right);

  return {
    count: state.count,
    min: state.min,
    max: state.max,
    sum: state.sum,
    avg: state.count > 0 ? state.sum / state.count : null,
    p50: quantileFromSorted(sorted, 0.5),
    p95: quantileFromSorted(sorted, 0.95),
    p99: quantileFromSorted(sorted, 0.99),
  };
};

const cloneStatus = (status: TelemetryStatus): TelemetryStatus => ({ ...status });

const getBrowserConnectionInfo = (): BrowserConnectionInfo => {
  if (typeof navigator === "undefined") {
    return {
      effectiveType: null,
      downlinkMbps: null,
      rttMs: null,
    };
  }

  const navigatorWithConnection = navigator as Navigator & {
    connection?: {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
    };
    mozConnection?: {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
    };
    webkitConnection?: {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
    };
  };

  const connection =
    navigatorWithConnection.connection ??
    navigatorWithConnection.mozConnection ??
    navigatorWithConnection.webkitConnection ??
    null;

  return {
    effectiveType: connection?.effectiveType ?? null,
    downlinkMbps: typeof connection?.downlink === "number" ? connection.downlink : null,
    rttMs: typeof connection?.rtt === "number" ? connection.rtt : null,
  };
};

const getBrowserOnline = (): boolean =>
  typeof navigator === "undefined" ? true : navigator.onLine !== false;

const toEventTags = (
  data: Record<string, unknown> | undefined
): TelemetryTags | undefined => {
  if (!data) {
    return undefined;
  }

  const tags: TelemetryTags = {};
  Object.keys(data).forEach((key) => {
    const value = data[key];
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      tags[key] = value;
    }
  });

  return Object.keys(tags).length > 0 ? tags : undefined;
};

class TelemetryStore implements TelemetryApi {
  private readonly provider: TranscriptionMetricsProvider;
  private readonly capabilities: TelemetryCapabilities;
  private readonly rowLimit: number;
  private readonly onRow?: TelemetryRowCallback;
  private readonly counters = new Map<string, number>();
  private readonly gauges = new Map<string, number>();
  private readonly histograms = new Map<string, HistogramState>();
  private readonly listeners = new Set<(row: TelemetryRow) => void>();
  private readonly rows: TelemetryRow[] = [];
  private rowStart = 0;
  private revision = 0;
  private sessionId = createSessionId();
  private sessionStartedAt: number | null = null;
  private sessionEndedAt: number | null = null;
  private status: TelemetryStatus;
  private snapshotCache: TelemetrySnapshot | null = null;
  private snapshotRevision = -1;
  private snapshotAt = 0;

  constructor(options: {
    provider: TranscriptionMetricsProvider;
    capabilities: TelemetryCapabilities;
    rowLimit?: number;
    onRow?: TelemetryRowCallback;
    now?: number;
  }) {
    this.provider = options.provider;
    this.capabilities = options.capabilities;
    this.rowLimit = Math.max(options.rowLimit ?? DEFAULT_ROW_LIMIT, 1);
    this.onRow = options.onRow;

    const now = options.now ?? Date.now();
    this.status = {
      connectionState: null,
      websocketState: null,
      browserOnline: getBrowserOnline(),
      isRecovering: null,
      isBufferingAudio: null,
      isDrainingAudio: null,
      transportBufferedAmountBytes: 0,
      pendingBufferedAudioBytes: 0,
      persistedBufferedAudioBytes: 0,
      persistedBufferedAudioSegments: 0,
      totalBufferedAudioBytes: 0,
      lastDrainProgressAt: null,
      drainCycles: 0,
      drainExitReason: null,
      reconnectAttempt: null,
      nextReconnectDelayMs: null,
      remainingReconnectAttempts: null,
      finishDeliveryState: null,
      lastDisconnectCode: null,
      lastDisconnectReason: null,
      networkEffectiveType: getBrowserConnectionInfo().effectiveType,
    };
    this.snapshotAt = now;
  }

  public startSession(_meta?: Record<string, unknown>): string {
    this.sessionId = createSessionId();
    this.sessionStartedAt = Date.now();
    this.sessionEndedAt = null;
    this.bumpRevision();
    return this.sessionId;
  }

  public endSession() {
    if (this.sessionStartedAt !== null && this.sessionEndedAt === null) {
      this.sessionEndedAt = Date.now();
      this.bumpRevision();
    }
  }

  public increment(name: string, value = 1, tags?: TelemetryTags): void {
    const next = (this.counters.get(name) ?? 0) + value;
    this.counters.set(name, next);
    this.appendRow(name, value, "counter", tags);
    this.bumpRevision();
  }

  public gauge(
    name: string,
    value: number,
    tags?: TelemetryTags,
    emitRow = true
  ): void {
    const previousValue = this.gauges.get(name);
    const changed = previousValue !== value;
    this.gauges.set(name, value);
    if (emitRow) {
      this.appendRow(name, value, "gauge", tags);
    }
    if (changed) {
      this.bumpRevision();
    }
  }

  public observe(name: string, value: number, tags?: TelemetryTags): void {
    let state = this.histograms.get(name);
    if (!state) {
      state = {
        count: 0,
        sum: 0,
        min: null,
        max: null,
        sample: [],
        sampleCursor: 0,
      };
      this.histograms.set(name, state);
    }

    state.count += 1;
    state.sum += value;
    state.min = state.min === null ? value : Math.min(state.min, value);
    state.max = state.max === null ? value : Math.max(state.max, value);

    if (state.sample.length < HISTOGRAM_SAMPLE_SIZE) {
      state.sample.push(value);
    } else {
      state.sample[state.sampleCursor] = value;
      state.sampleCursor = (state.sampleCursor + 1) % HISTOGRAM_SAMPLE_SIZE;
    }

    this.appendRow(name, value, "histogram", tags);
    this.bumpRevision();
  }

  public event(name: string, data?: Record<string, unknown>): void {
    this.appendRow(name, 1, "event", toEventTags(data));
    this.bumpRevision();
  }

  public setStatus(patch: Partial<TelemetryStatus>) {
    const next = {
      ...this.status,
      ...patch,
    };
    const changed = JSON.stringify(next) !== JSON.stringify(this.status);
    this.status = next;
    if (changed) {
      this.bumpRevision();
    }
  }

  public getStatus(): TelemetryStatus {
    return cloneStatus(this.status);
  }

  public getCounter(name: string): number {
    return this.counters.get(name) ?? 0;
  }

  public getGauge(name: string): number | null {
    const value = this.gauges.get(name);
    return typeof value === "number" ? value : null;
  }

  public getHistogram(name: string): TelemetryHistogramSummary | null {
    const histogram = this.histograms.get(name);
    return histogram ? summarizeHistogram(histogram) : null;
  }

  public getTelemetrySnapshot(now = Date.now()): TelemetrySnapshot {
    const isSessionActive = this.sessionStartedAt !== null && this.sessionEndedAt === null;

    if (
      this.snapshotCache &&
      this.snapshotRevision === this.revision &&
      (!isSessionActive || this.snapshotAt === now)
    ) {
      return this.snapshotCache;
    }

    const counters: Record<string, number> = {};
    const gauges: Record<string, number> = {};
    const histograms: Record<string, TelemetryHistogramSummary> = {};

    this.counters.forEach((value, key) => {
      counters[key] = value;
    });
    this.gauges.forEach((value, key) => {
      gauges[key] = value;
    });
    this.histograms.forEach((value, key) => {
      histograms[key] = summarizeHistogram(value);
    });

    const produced = counters["audio.chunks.produced"] ?? 0;
    const sent = counters["audio.chunks.sent"] ?? 0;
    const dropped = counters["audio.chunks.dropped"] ?? 0;
    const reconnects = counters["ws.reconnects"] ?? 0;
    const recoveryCompleted = counters["recovery.completed.count"] ?? 0;
    const recoveryTime = histograms["recovery.time_ms"];
    const stallMs = counters["tx.stall.time_ms"] ?? 0;
    const pressureMs = counters["ws.buffer_pressure.time_ms"] ?? 0;
    const startedAt = this.sessionStartedAt;
    const endedAt = this.sessionEndedAt;
    const durationMs =
      startedAt === null
        ? null
        : Math.max((endedAt ?? now) - startedAt, 0);

    const ratio = (left: number, right: number): number | null =>
      right > 0 ? left / right : null;

    const snapshot: TelemetrySnapshot = {
      schemaVersion: 2,
      updatedAt: now,
      provider: this.provider,
      capabilities: { ...this.capabilities },
      window: {
        sessionId: this.sessionId,
        startedAt,
        endedAt,
        durationMs,
      },
      status: cloneStatus(this.status),
      counters,
      gauges,
      histograms,
      derived: {
        continuity_loss_rate: ratio(dropped, produced),
        delivery_rate: ratio(sent, produced),
        reconnect_success_rate: ratio(recoveryCompleted, reconnects),
        mean_recovery_time_ms: recoveryTime?.avg ?? null,
        stall_ratio: durationMs && durationMs > 0 ? stallMs / durationMs : null,
        buffer_pressure_ratio:
          durationMs && durationMs > 0 ? pressureMs / durationMs : null,
      },
    };

    this.snapshotCache = snapshot;
    this.snapshotRevision = this.revision;
    this.snapshotAt = now;

    return snapshot;
  }

  public getTelemetryRows(): TelemetryRow[] {
    if (this.rows.length < this.rowLimit || this.rowStart === 0) {
      return this.rows.slice();
    }

    return this.rows.slice(this.rowStart).concat(this.rows.slice(0, this.rowStart));
  }

  public clearTelemetryRows(): void {
    this.rows.length = 0;
    this.rowStart = 0;
    this.bumpRevision();
  }

  public resetTelemetry(): void {
    this.counters.clear();
    this.gauges.clear();
    this.histograms.clear();
    this.clearTelemetryRows();
    this.sessionId = createSessionId();
    this.sessionStartedAt = null;
    this.sessionEndedAt = null;
    this.bumpRevision();
  }

  public subscribe(cb: (row: TelemetryRow) => void): () => void {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  private appendRow(
    metric: string,
    value: number,
    type: TelemetryRow["type"],
    tags?: TelemetryTags
  ) {
    const row: TelemetryRow = {
      ts: Date.now(),
      sessionId: this.sessionId,
      metric,
      value,
      type,
      tags,
    };

    if (this.rows.length < this.rowLimit) {
      this.rows.push(row);
    } else {
      this.rows[this.rowStart] = row;
      this.rowStart = (this.rowStart + 1) % this.rowLimit;
    }

    this.listeners.forEach((listener) => listener(row));
    if (this.onRow) {
      this.onRow(row);
    }
  }

  private bumpRevision() {
    this.revision += 1;
  }
}

export const createUnsupportedTelemetrySnapshot = (
  provider: TranscriptionMetricsProvider,
  now = Date.now()
): TelemetrySnapshot => ({
  schemaVersion: 2,
  updatedAt: now,
  provider,
  capabilities: {
    transcriptUi: false,
    session: false,
    connection: false,
    recovery: false,
    buffering: false,
    browserNetwork: false,
    audioCapture: false,
  },
  window: {
    sessionId: createSessionId(),
    startedAt: null,
    endedAt: null,
    durationMs: null,
  },
  status: {
    connectionState: "disconnected",
    websocketState: "unavailable",
    browserOnline: getBrowserOnline(),
    isRecovering: false,
    isBufferingAudio: false,
    isDrainingAudio: false,
    transportBufferedAmountBytes: 0,
    pendingBufferedAudioBytes: 0,
    persistedBufferedAudioBytes: 0,
    persistedBufferedAudioSegments: 0,
    totalBufferedAudioBytes: 0,
    lastDrainProgressAt: null,
    drainCycles: 0,
    drainExitReason: null,
    reconnectAttempt: null,
    nextReconnectDelayMs: null,
    remainingReconnectAttempts: null,
    finishDeliveryState: null,
    lastDisconnectCode: null,
    lastDisconnectReason: null,
    networkEffectiveType: null,
  },
  counters: {},
  gauges: {},
  histograms: {},
  derived: {
    continuity_loss_rate: null,
    delivery_rate: null,
    reconnect_success_rate: null,
    mean_recovery_time_ms: null,
    stall_ratio: null,
    buffer_pressure_ratio: null,
  },
});

export class WhisperTelemetryCollector {
  private readonly provider: TranscriptionMetricsProvider;
  private readonly onEmit: MetricsEmitCallback;
  private readonly advancedMetrics: boolean;
  private readonly throttleMs: number;
  private readonly createdAt: number;
  private readonly store: TelemetryStore;
  private emitTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
  private lastEmittedAt: number | null = null;
  private initializedLatencyRecorded = false;
  private sessionStartedAt: number | null = null;
  private pauseStartedAt: number | null = null;
  private pausedDurationBaseMs = 0;
  private bufferingStartedAt: number | null = null;
  private drainingStartedAt: number | null = null;
  private bufferingDurationBaseMs = 0;
  private drainingDurationBaseMs = 0;
  private pressureStartedAt: number | null = null;
  private recoveryStartedAt: number | null = null;
  private offlineStartedAt: number | null = null;
  private offlineDurationBaseMs = 0;
  private latestConnectionState:
    | "connecting"
    | "connected"
    | "reconnecting"
    | "disconnected"
    | "stopped"
    | null = null;
  private latestBrowserOnline = getBrowserOnline();
  private latestDrainExitReason:
    | "completed"
    | "socket_unavailable"
    | "send_failed"
    | "stalled"
    | "stopped"
    | null = null;
  private latestFinishDeliveryState: "sent" | "skipped_socket_closed" | "timed_out" | null =
    null;
  private maxQueuePeakBytes = 0;
  private effectiveType: string | null = getBrowserConnectionInfo().effectiveType;

  private inputSampleRate: number;
  private targetSampleRate: number;
  private channelCount: number;
  private currentRms = 0;
  private peakRms = 0;
  private currentPeakAmplitude = 0;
  private clippingEventCount = 0;
  private processedFrameCount = 0;
  private silentFrameCount = 0;
  private speechFrameCount = 0;
  private audioCallbackGapCount = 0;
  private longestAudioCallbackGapMs = 0;
  private lastAudioFrameAt: number | null = null;
  private speechActive = false;
  private silenceStartedAt: number | null = null;

  private utteranceCount = 0;
  private activeUtteranceStartedAt: number | null = null;
  private activeUtteranceFirstPartialAt: number | null = null;
  private lastPartialAt: number | null = null;
  private lastFinalAt: number | null = null;
  private lastFinalText: string | null = null;

  constructor(options: {
    provider: TranscriptionMetricsProvider;
    onEmit: MetricsEmitCallback;
    onRow?: TelemetryRowCallback;
    now?: number;
    throttleMs?: number;
    rowLimit?: number;
    advancedMetrics?: boolean;
    inputSampleRate?: number | null;
    targetSampleRate?: number | null;
    channelCount?: number | null;
  }) {
    this.provider = options.provider;
    this.onEmit = options.onEmit;
    this.advancedMetrics = options.advancedMetrics ?? true;
    this.throttleMs = options.throttleMs ?? DEFAULT_THROTTLE_MS;
    this.createdAt = options.now ?? Date.now();
    this.inputSampleRate = options.inputSampleRate ?? 44100;
    this.targetSampleRate = options.targetSampleRate ?? 16000;
    this.channelCount = options.channelCount ?? 1;

    this.store = new TelemetryStore({
      provider: this.provider,
      capabilities: {
        transcriptUi: true,
        session: true,
        connection: true,
        recovery: true,
        buffering: true,
        browserNetwork: true,
        audioCapture: this.advancedMetrics,
      },
      rowLimit: options.rowLimit ?? DEFAULT_ROW_LIMIT,
      onRow: options.onRow,
      now: this.createdAt,
    });

    this.store.setStatus({
      connectionState: "connecting",
      websocketState: "connecting",
      browserOnline: getBrowserOnline(),
      isRecovering: false,
      isBufferingAudio: false,
      isDrainingAudio: false,
      transportBufferedAmountBytes: 0,
      pendingBufferedAudioBytes: 0,
      persistedBufferedAudioBytes: 0,
      persistedBufferedAudioSegments: 0,
      totalBufferedAudioBytes: 0,
      lastDrainProgressAt: null,
      drainCycles: 0,
      drainExitReason: null,
      reconnectAttempt: null,
      nextReconnectDelayMs: null,
      remainingReconnectAttempts: null,
      finishDeliveryState: null,
      lastDisconnectCode: null,
      lastDisconnectReason: null,
      networkEffectiveType: this.effectiveType,
    });

    this.store.gauge("browser.online", getBrowserOnline() ? 1 : 0, undefined, false);
    if (this.advancedMetrics) {
      this.store.gauge("audio.capture.input_sample_rate", this.inputSampleRate, undefined, false);
      this.store.gauge("audio.capture.target_sample_rate", this.targetSampleRate, undefined, false);
      this.store.gauge("audio.capture.channel_count", this.channelCount, undefined, false);
    }
    this.queueEmit(this.createdAt, true);
  }

  public resetSession(now = Date.now()) {
    if (this.emitTimer !== null) {
      globalThis.clearTimeout(this.emitTimer);
      this.emitTimer = null;
    }

    this.store.resetTelemetry();
    this.sessionStartedAt = null;
    this.pauseStartedAt = null;
    this.pausedDurationBaseMs = 0;
    this.bufferingStartedAt = null;
    this.drainingStartedAt = null;
    this.bufferingDurationBaseMs = 0;
    this.drainingDurationBaseMs = 0;
    this.pressureStartedAt = null;
    this.recoveryStartedAt = null;
    this.offlineStartedAt = null;
    this.offlineDurationBaseMs = 0;
    this.latestConnectionState = "connecting";
    this.latestBrowserOnline = getBrowserOnline();
    this.latestDrainExitReason = null;
    this.latestFinishDeliveryState = null;
    this.maxQueuePeakBytes = 0;

    this.currentRms = 0;
    this.peakRms = 0;
    this.currentPeakAmplitude = 0;
    this.clippingEventCount = 0;
    this.processedFrameCount = 0;
    this.silentFrameCount = 0;
    this.speechFrameCount = 0;
    this.audioCallbackGapCount = 0;
    this.longestAudioCallbackGapMs = 0;
    this.lastAudioFrameAt = null;
    this.speechActive = false;
    this.silenceStartedAt = null;

    this.utteranceCount = 0;
    this.activeUtteranceStartedAt = null;
    this.activeUtteranceFirstPartialAt = null;
    this.lastPartialAt = null;
    this.lastFinalAt = null;
    this.lastFinalText = null;

    this.store.setStatus({
      connectionState: "connecting",
      websocketState: "connecting",
      browserOnline: this.latestBrowserOnline,
      isRecovering: false,
      isBufferingAudio: false,
      isDrainingAudio: false,
      transportBufferedAmountBytes: 0,
      pendingBufferedAudioBytes: 0,
      persistedBufferedAudioBytes: 0,
      persistedBufferedAudioSegments: 0,
      totalBufferedAudioBytes: 0,
      lastDrainProgressAt: null,
      drainCycles: 0,
      drainExitReason: null,
      reconnectAttempt: null,
      nextReconnectDelayMs: null,
      remainingReconnectAttempts: null,
      finishDeliveryState: null,
      lastDisconnectCode: null,
      lastDisconnectReason: null,
      networkEffectiveType: this.effectiveType,
    });

    this.store.gauge("browser.online", this.latestBrowserOnline ? 1 : 0, undefined, false);
    this.lastEmittedAt = null;
    this.queueEmit(now, true);
  }

  public markReady(now = Date.now()) {
    if (!this.initializedLatencyRecorded) {
      const initializationLatencyMs = Math.max(now - this.createdAt, 0);
      this.store.observe("session.initialization_latency_ms", initializationLatencyMs);
      this.initializedLatencyRecorded = true;
      this.queueEmit(now, true);
      return;
    }

    this.queueEmit(now, false);
  }

  public markSessionStart(now = Date.now()) {
    this.store.startSession();
    this.sessionStartedAt = now;
    this.store.increment("session.start.count");
    this.queueEmit(now, true);
  }

  public markSessionPause(now = Date.now()) {
    if (this.pauseStartedAt === null) {
      this.pauseStartedAt = now;
      this.store.increment("session.pause.count");
      this.queueEmit(now, true);
    }
  }

  public markSessionResume(now = Date.now()) {
    this.store.increment("session.resume.count");

    if (this.pauseStartedAt !== null) {
      this.pausedDurationBaseMs += Math.max(now - this.pauseStartedAt, 0);
      this.pauseStartedAt = null;
    }

    this.queueEmit(now, true);
  }

  public markSessionStop(now = Date.now()) {
    if (this.pauseStartedAt !== null) {
      this.pausedDurationBaseMs += Math.max(now - this.pauseStartedAt, 0);
      this.pauseStartedAt = null;
    }

    if (this.pressureStartedAt !== null) {
      this.store.increment(
        "ws.buffer_pressure.time_ms",
        Math.max(now - this.pressureStartedAt, 0)
      );
      this.pressureStartedAt = null;
    }

    this.store.endSession();
    this.queueEmit(now, true);
  }

  public markSessionError(fatal = false, now = Date.now()) {
    this.store.increment("session.error.count");
    if (fatal) {
      this.store.increment("session.fatal_error.count");
    }
    this.queueEmit(now, true);
  }

  public markAudioChunkProduced(sizeBytes: number, now = Date.now()) {
    this.store.increment("audio.chunks.produced");
    this.store.observe("audio.chunk.size_bytes", sizeBytes);
    this.queueEmit(now, false);
  }

  public markAudioChunkSent(sizeBytes: number, now = Date.now()) {
    this.store.increment("audio.chunks.sent");
    this.store.increment("ws.messages.sent");
    this.store.observe("audio.sent.size_bytes", sizeBytes);
    this.queueEmit(now, false);
  }

  public markAudioChunkDropped(sizeBytes: number, reason: string, now = Date.now()) {
    this.store.increment("audio.chunks.dropped", 1, { reason });
    this.store.increment("recovery.data_lost.bytes", sizeBytes);
    this.queueEmit(now, true);
  }

  public recordTranscriptPartial(text: string, now = Date.now()) {
    this.store.increment("tx.partial.received");

    if (this.lastPartialAt !== null) {
      this.store.observe("tx.partial.inter_arrival_ms", Math.max(now - this.lastPartialAt, 0));
    }
    this.lastPartialAt = now;

    if (this.activeUtteranceStartedAt === null) {
      this.activeUtteranceStartedAt = now;
      this.activeUtteranceFirstPartialAt = null;
      this.utteranceCount += 1;
      this.store.increment("tx.utterance.count");
    }

    if (this.activeUtteranceFirstPartialAt === null) {
      this.activeUtteranceFirstPartialAt = now;
      const latency = Math.max(now - this.activeUtteranceStartedAt, 0);
      this.store.observe("tx.first_partial.time_ms", latency);
    }

    this.queueEmit(now, false);
  }

  public recordTranscriptFinal(text: string, now = Date.now()) {
    this.store.increment("tx.final.received");

    if (this.lastFinalAt !== null) {
      this.store.observe("tx.final.inter_arrival_ms", Math.max(now - this.lastFinalAt, 0));
    }
    this.lastFinalAt = now;

    if (this.lastFinalText && this.lastFinalText === text) {
      this.store.increment("tx.duplicate_segments");
    }
    this.lastFinalText = text;

    if (this.activeUtteranceStartedAt !== null) {
      this.store.observe("tx.first_final.time_ms", Math.max(now - this.activeUtteranceStartedAt, 0));
    }
    if (this.activeUtteranceFirstPartialAt !== null) {
      this.store.observe(
        "tx.stabilization.time_ms",
        Math.max(now - this.activeUtteranceFirstPartialAt, 0)
      );
    }

    this.activeUtteranceStartedAt = null;
    this.activeUtteranceFirstPartialAt = null;

    this.queueEmit(now, true);
  }

  public recordTranscriptDiarization(now = Date.now()) {
    this.store.increment("tx.diarization.received");
    this.queueEmit(now, false);
  }

  public recordAudioFrame(sampleData: Float32Array, now = Date.now()) {
    if (!this.advancedMetrics) {
      return;
    }

    if (sampleData.length === 0) {
      return;
    }

    let squaredSum = 0;
    let peakAmplitude = 0;

    for (let index = 0; index < sampleData.length; index += 1) {
      const sample = sampleData[index];
      const amplitude = Math.abs(sample);
      squaredSum += sample * sample;
      peakAmplitude = Math.max(peakAmplitude, amplitude);
    }

    this.processedFrameCount += 1;
    this.currentRms = Math.sqrt(squaredSum / sampleData.length);
    this.peakRms = Math.max(this.peakRms, this.currentRms);
    this.currentPeakAmplitude = peakAmplitude;

    if (peakAmplitude >= DEFAULT_AUDIO_METRICS.clippingThreshold) {
      this.clippingEventCount += 1;
      this.store.increment("audio.capture.clipping_events");
    }

    if (this.lastAudioFrameAt !== null) {
      const gapMs = Math.max(now - this.lastAudioFrameAt, 0);
      if (gapMs >= DEFAULT_AUDIO_METRICS.callbackGapThresholdMs) {
        this.audioCallbackGapCount += 1;
        this.longestAudioCallbackGapMs = Math.max(this.longestAudioCallbackGapMs, gapMs);
        this.store.increment("audio.capture.gap.count");
        this.store.observe("audio.capture.gap_ms", gapMs);
      }
    }

    this.lastAudioFrameAt = now;

    const isSpeechFrame = this.currentRms >= DEFAULT_AUDIO_METRICS.speechRmsThreshold;
    if (isSpeechFrame) {
      this.speechFrameCount += 1;
    } else {
      this.silentFrameCount += 1;
    }

    if (isSpeechFrame) {
      if (!this.speechActive) {
        this.speechActive = true;
        if (this.activeUtteranceStartedAt === null) {
          this.activeUtteranceStartedAt = now;
          this.activeUtteranceFirstPartialAt = null;
          this.utteranceCount += 1;
          this.store.increment("tx.utterance.count");
        }
      }
      this.silenceStartedAt = null;
    } else if (this.speechActive) {
      if (this.silenceStartedAt === null) {
        this.silenceStartedAt = now;
      } else if (now - this.silenceStartedAt >= DEFAULT_AUDIO_METRICS.utteranceEndSilenceMs) {
        this.speechActive = false;
        this.silenceStartedAt = null;
      }
    }

    this.queueEmit(now, false);
  }

  public recordResilienceStatus(status: ResilienceStatusLike, now = Date.now()) {
    const previousConnectionState = this.latestConnectionState;
    const previousBrowserOnline = this.latestBrowserOnline;
    const previousDrainExitReason = this.latestDrainExitReason;
    const previousFinishDeliveryState = this.latestFinishDeliveryState;

    if (status.connectionState !== previousConnectionState) {
      if (status.connectionState === "connecting") {
        this.store.increment("ws.connect.attempts");
      }

      if (status.connectionState === "connected") {
        this.store.increment("ws.connect.success");
        if (previousConnectionState === "reconnecting") {
          this.store.increment("recovery.completed.count");
          if (this.recoveryStartedAt !== null) {
            const recoveryDurationMs = Math.max(now - this.recoveryStartedAt, 0);
            this.store.observe("recovery.time_ms", recoveryDurationMs);
            this.store.observe("ws.reconnect.time_ms", recoveryDurationMs);
          }
          this.recoveryStartedAt = null;
        }
      }

      if (
        previousConnectionState === "connected" &&
        status.connectionState !== "connected"
      ) {
        this.store.increment("ws.disconnects");
      }

      if (status.connectionState === "reconnecting") {
        this.store.increment("ws.reconnects");
        this.store.increment("recovery.started.count");
        this.recoveryStartedAt = now;
      }

      if (
        status.connectionState === "disconnected" &&
        previousConnectionState === "reconnecting"
      ) {
        this.store.increment("recovery.failed.count");
        this.recoveryStartedAt = null;
      }
    }

    if (status.lastDisconnect?.errorMessage) {
      this.store.increment("ws.errors");
    }

    if (status.transportBufferedAmountBytes > 0) {
      if (this.pressureStartedAt === null) {
        this.pressureStartedAt = now;
      }
    } else if (this.pressureStartedAt !== null) {
      this.store.increment(
        "ws.buffer_pressure.time_ms",
        Math.max(now - this.pressureStartedAt, 0)
      );
      this.pressureStartedAt = null;
    }

    this.maxQueuePeakBytes = Math.max(
      this.maxQueuePeakBytes,
      status.transportBufferedAmountBytes
    );

    if (this.bufferingStartedAt !== null && !status.isBufferingAudio) {
      this.bufferingDurationBaseMs += Math.max(now - this.bufferingStartedAt, 0);
      this.bufferingStartedAt = null;
    } else if (this.bufferingStartedAt === null && status.isBufferingAudio) {
      this.bufferingStartedAt = now;
    }

    if (this.drainingStartedAt !== null && !status.isDrainingAudio) {
      this.drainingDurationBaseMs += Math.max(now - this.drainingStartedAt, 0);
      this.drainingStartedAt = null;
    } else if (this.drainingStartedAt === null && status.isDrainingAudio) {
      this.drainingStartedAt = now;
    }

    if (status.drainExitReason === "stalled" && previousDrainExitReason !== "stalled") {
      this.store.increment("buffer.stall.count");
    }

    if (
      status.finishDeliveryState === "timed_out" &&
      previousFinishDeliveryState !== "timed_out" &&
      status.totalBufferedAudioBytes > 0
    ) {
      this.store.increment("recovery.data_lost.bytes", status.totalBufferedAudioBytes);
    }

    if (!status.browserOnline && previousBrowserOnline) {
      this.store.increment("browser.offline.events");
      this.offlineStartedAt = now;
    }

    if (status.browserOnline && !previousBrowserOnline) {
      this.store.increment("browser.online.events");
      this.store.increment("browser.flap.count");
      if (this.offlineStartedAt !== null) {
        this.offlineDurationBaseMs += Math.max(now - this.offlineStartedAt, 0);
        this.offlineStartedAt = null;
      }
    }

    const connectionInfo = getBrowserConnectionInfo();
    if (typeof connectionInfo.rttMs === "number") {
      this.store.gauge("network.rtt_ms", connectionInfo.rttMs);
    }
    if (typeof connectionInfo.downlinkMbps === "number") {
      this.store.gauge("network.downlink_mbps", connectionInfo.downlinkMbps);
    }
    if (connectionInfo.effectiveType !== this.effectiveType) {
      this.effectiveType = connectionInfo.effectiveType;
      this.store.event("network.effective_type_changed", {
        value: this.effectiveType ?? "unknown",
      });
    }

    this.store.gauge("ws.buffered_amount.bytes", status.transportBufferedAmountBytes);
    this.store.gauge("ws.send.queue_peak.bytes", this.maxQueuePeakBytes, undefined, false);
    this.store.gauge("recovery.backlog.bytes", status.totalBufferedAudioBytes, undefined, false);
    this.store.gauge(
      "recovery.backlog.chunks",
      status.persistedBufferedAudioSegments,
      undefined,
      false
    );
    this.store.gauge("browser.online", status.browserOnline ? 1 : 0, undefined, false);

    this.store.setStatus({
      connectionState: status.connectionState,
      websocketState: status.websocketState,
      browserOnline: status.browserOnline,
      isRecovering: status.isRecovering,
      isBufferingAudio: status.isBufferingAudio,
      isDrainingAudio: status.isDrainingAudio,
      transportBufferedAmountBytes: status.transportBufferedAmountBytes,
      pendingBufferedAudioBytes: status.pendingBufferedAudioBytes,
      persistedBufferedAudioBytes: status.persistedBufferedAudioBytes,
      persistedBufferedAudioSegments: status.persistedBufferedAudioSegments,
      totalBufferedAudioBytes: status.totalBufferedAudioBytes,
      lastDrainProgressAt: status.lastDrainProgressAt,
      drainCycles: status.drainCycles,
      drainExitReason: status.drainExitReason,
      reconnectAttempt: status.reconnectAttempt,
      nextReconnectDelayMs: status.nextReconnectDelayMs,
      remainingReconnectAttempts: status.remainingReconnectAttempts,
      finishDeliveryState: status.finishDeliveryState,
      lastDisconnectCode: status.lastDisconnect?.code ?? null,
      lastDisconnectReason:
        status.lastDisconnect?.reason ?? status.lastDisconnect?.errorMessage ?? null,
      networkEffectiveType: this.effectiveType,
    });

    this.latestConnectionState = status.connectionState;
    this.latestBrowserOnline = status.browserOnline;
    this.latestDrainExitReason = status.drainExitReason;
    this.latestFinishDeliveryState = status.finishDeliveryState;

    const important =
      previousConnectionState !== status.connectionState ||
      previousBrowserOnline !== status.browserOnline ||
      status.drainExitReason !== previousDrainExitReason ||
      status.finishDeliveryState !== previousFinishDeliveryState;

    this.queueEmit(now, important);
  }

  public recordBrowserOnline(now = Date.now()) {
    const current = this.store.getStatus();
    this.recordResilienceStatus(
      {
        connectionState: current.connectionState ?? "connecting",
        websocketState: current.websocketState ?? "connecting",
        browserOnline: true,
        isRecovering: current.isRecovering ?? false,
        isBufferingAudio: current.isBufferingAudio ?? false,
        isDrainingAudio: current.isDrainingAudio ?? false,
        transportBufferedAmountBytes: current.transportBufferedAmountBytes ?? 0,
        pendingBufferedAudioBytes: current.pendingBufferedAudioBytes ?? 0,
        persistedBufferedAudioBytes: current.persistedBufferedAudioBytes ?? 0,
        persistedBufferedAudioSegments: current.persistedBufferedAudioSegments ?? 0,
        totalBufferedAudioBytes: current.totalBufferedAudioBytes ?? 0,
        bufferingStartedAt: null,
        drainStartedAt: null,
        lastDrainProgressAt: current.lastDrainProgressAt,
        drainCycles: current.drainCycles ?? 0,
        drainExitReason: current.drainExitReason,
        reconnectAttempt: current.reconnectAttempt,
        nextReconnectDelayMs: current.nextReconnectDelayMs,
        finishDeliveryState: current.finishDeliveryState,
        maxReconnectAttempts: 0,
        remainingReconnectAttempts: current.remainingReconnectAttempts,
        lastDisconnect: {
          code: current.lastDisconnectCode ?? undefined,
          reason: current.lastDisconnectReason ?? undefined,
        },
      },
      now
    );
  }

  public recordBrowserOffline(now = Date.now()) {
    const current = this.store.getStatus();
    this.recordResilienceStatus(
      {
        connectionState: current.connectionState ?? "connecting",
        websocketState: current.websocketState ?? "connecting",
        browserOnline: false,
        isRecovering: current.isRecovering ?? false,
        isBufferingAudio: current.isBufferingAudio ?? false,
        isDrainingAudio: current.isDrainingAudio ?? false,
        transportBufferedAmountBytes: current.transportBufferedAmountBytes ?? 0,
        pendingBufferedAudioBytes: current.pendingBufferedAudioBytes ?? 0,
        persistedBufferedAudioBytes: current.persistedBufferedAudioBytes ?? 0,
        persistedBufferedAudioSegments: current.persistedBufferedAudioSegments ?? 0,
        totalBufferedAudioBytes: current.totalBufferedAudioBytes ?? 0,
        bufferingStartedAt: null,
        drainStartedAt: null,
        lastDrainProgressAt: current.lastDrainProgressAt,
        drainCycles: current.drainCycles ?? 0,
        drainExitReason: current.drainExitReason,
        reconnectAttempt: current.reconnectAttempt,
        nextReconnectDelayMs: current.nextReconnectDelayMs,
        finishDeliveryState: current.finishDeliveryState,
        maxReconnectAttempts: 0,
        remainingReconnectAttempts: current.remainingReconnectAttempts,
        lastDisconnect: {
          code: current.lastDisconnectCode ?? undefined,
          reason: current.lastDisconnectReason ?? undefined,
        },
      },
      now
    );
  }

  public getTelemetrySnapshot(now = Date.now()): TelemetrySnapshot {
    this.syncComputedMetrics(now);
    return this.store.getTelemetrySnapshot(now);
  }

  public getTelemetryRows(): TelemetryRow[] {
    return this.store.getTelemetryRows();
  }

  public clearTelemetryRows() {
    this.store.clearTelemetryRows();
  }

  public resetTelemetry(now = Date.now()) {
    this.resetSession(now);
    this.markSessionStart(now);
  }

  public flush(now = Date.now()) {
    this.emitNow(now);
  }

  public dispose() {
    if (this.emitTimer !== null) {
      globalThis.clearTimeout(this.emitTimer);
      this.emitTimer = null;
    }
  }

  private syncComputedMetrics(now: number) {
    const pausedDurationMs =
      this.pausedDurationBaseMs +
      (this.pauseStartedAt !== null ? Math.max(now - this.pauseStartedAt, 0) : 0);
    const bufferingDurationMs =
      this.bufferingDurationBaseMs +
      (this.bufferingStartedAt !== null ? Math.max(now - this.bufferingStartedAt, 0) : 0);
    const drainingDurationMs =
      this.drainingDurationBaseMs +
      (this.drainingStartedAt !== null ? Math.max(now - this.drainingStartedAt, 0) : 0);
    const offlineDurationMs =
      this.offlineDurationBaseMs +
      (this.offlineStartedAt !== null ? Math.max(now - this.offlineStartedAt, 0) : 0);

    const totalFrames = Math.max(this.processedFrameCount, 1);
    const silenceRatio = this.silentFrameCount / totalFrames;
    const speechActivityRatio = this.speechFrameCount / totalFrames;

    this.store.gauge("session.paused_duration_ms", pausedDurationMs, undefined, false);
    this.store.gauge("buffering.duration_ms", bufferingDurationMs, undefined, false);
    this.store.gauge("draining.duration_ms", drainingDurationMs, undefined, false);
    this.store.gauge("browser.offline.duration_ms", offlineDurationMs, undefined, false);
    if (this.advancedMetrics) {
      this.store.gauge("audio.current_rms", this.currentRms, undefined, false);
      this.store.gauge("audio.peak_rms", this.peakRms, undefined, false);
      this.store.gauge(
        "audio.current_peak_amplitude",
        this.currentPeakAmplitude,
        undefined,
        false
      );
      this.store.gauge("audio.capture.silence_ratio", silenceRatio, undefined, false);
      this.store.gauge(
        "audio.capture.speech_activity_ratio",
        speechActivityRatio,
        undefined,
        false
      );
      this.store.gauge(
        "audio.capture.gap.longest_ms",
        this.longestAudioCallbackGapMs,
        undefined,
        false
      );
    }
  }

  private queueEmit(now: number, immediate: boolean) {
    if (immediate || this.lastEmittedAt === null || now - this.lastEmittedAt >= this.throttleMs) {
      this.emitNow(now);
      return;
    }

    if (this.emitTimer !== null) {
      return;
    }

    const delay = Math.max(this.throttleMs - (now - this.lastEmittedAt), 0);
    this.emitTimer = globalThis.setTimeout(() => {
      this.emitTimer = null;
      this.emitNow(Date.now());
    }, delay);
  }

  private emitNow(now: number) {
    if (this.emitTimer !== null) {
      globalThis.clearTimeout(this.emitTimer);
      this.emitTimer = null;
    }

    this.syncComputedMetrics(now);
    this.lastEmittedAt = now;
    this.onEmit(this.store.getTelemetrySnapshot(now));
  }
}

const getCounter = (snapshot: TelemetrySnapshot, name: string): number =>
  snapshot.counters[name] ?? 0;

const getGauge = (snapshot: TelemetrySnapshot, name: string): number | null => {
  const value = snapshot.gauges[name];
  return typeof value === "number" ? value : null;
};

const getHistogram = (snapshot: TelemetrySnapshot, name: string): TelemetryHistogramSummary | null => {
  return snapshot.histograms[name] ?? null;
};

export const telemetryToLegacyMetricsSnapshot = (
  snapshot: TelemetrySnapshot
): TranscriptionMetricsSnapshot => {
  const firstPartial = getHistogram(snapshot, "tx.first_partial.time_ms");
  const firstFinal = getHistogram(snapshot, "tx.first_final.time_ms");
  const stabilization = getHistogram(snapshot, "tx.stabilization.time_ms");
  const recovery = getHistogram(snapshot, "recovery.time_ms");
  const pausedDuration = getGauge(snapshot, "session.paused_duration_ms");
  const bufferingDuration = getGauge(snapshot, "buffering.duration_ms");
  const drainingDuration = getGauge(snapshot, "draining.duration_ms");
  const offlineDuration = getGauge(snapshot, "browser.offline.duration_ms");
  const offlineCount = getCounter(snapshot, "browser.offline.events");
  const onlineCount = getCounter(snapshot, "browser.online.events");
  const flapCount = getCounter(snapshot, "browser.flap.count");

  return {
    schemaVersion: 1,
    updatedAt: snapshot.updatedAt,
    provider: snapshot.provider,
    transcriptUi: {
      supported: snapshot.capabilities.transcriptUi,
      currentPartialText: null,
      lastFinalText: null,
      partialCount: getCounter(snapshot, "tx.partial.received"),
      finalCount: getCounter(snapshot, "tx.final.received"),
      diarizationCount: getCounter(snapshot, "tx.diarization.received"),
      utteranceCount: getCounter(snapshot, "tx.utterance.count"),
      lastFirstPartialLatencyMs: firstPartial?.p95 ?? null,
      avgFirstPartialLatencyMs: firstPartial?.avg ?? null,
      p95FirstPartialLatencyMs: firstPartial?.p95 ?? null,
      lastFinalLatencyMs: firstFinal?.p95 ?? null,
      avgFinalLatencyMs: firstFinal?.avg ?? null,
      p95FinalLatencyMs: firstFinal?.p95 ?? null,
      lastStabilizationLatencyMs: stabilization?.p95 ?? null,
      avgStabilizationLatencyMs: stabilization?.avg ?? null,
      p95StabilizationLatencyMs: stabilization?.p95 ?? null,
    },
    session: {
      supported: snapshot.capabilities.session,
      sessionStartedAt: snapshot.window.startedAt,
      sessionStoppedAt: snapshot.window.endedAt,
      sessionDurationMs: snapshot.window.durationMs,
      initializationLatencyMs:
        getHistogram(snapshot, "session.initialization_latency_ms")?.avg ?? null,
      pauseCount: getCounter(snapshot, "session.pause.count"),
      resumeCount: getCounter(snapshot, "session.resume.count"),
      pausedDurationMs: pausedDuration,
      errorCount: getCounter(snapshot, "session.error.count"),
      fatalErrorCount: getCounter(snapshot, "session.fatal_error.count"),
    },
    connection: {
      supported: snapshot.capabilities.connection,
      connectionState: snapshot.status.connectionState,
      websocketState: snapshot.status.websocketState,
      browserOnline: snapshot.status.browserOnline,
      connectionOpenCount: getCounter(snapshot, "ws.connect.success"),
      connectionCloseCount: getCounter(snapshot, "ws.disconnects"),
      connectedDurationMs: null,
      reconnectingDurationMs: null,
      disconnectedDurationMs: null,
      transportBufferedAmountBytes: snapshot.status.transportBufferedAmountBytes,
      lastDisconnectCode: snapshot.status.lastDisconnectCode,
      lastDisconnectReason: snapshot.status.lastDisconnectReason,
    },
    recovery: {
      supported: snapshot.capabilities.recovery,
      reconnectCount: getCounter(snapshot, "ws.reconnects"),
      recoverySuccessCount: getCounter(snapshot, "recovery.completed.count"),
      terminalDisconnectCount: getCounter(snapshot, "recovery.failed.count"),
      currentReconnectAttempt: snapshot.status.reconnectAttempt,
      nextReconnectDelayMs: snapshot.status.nextReconnectDelayMs,
      remainingReconnectAttempts: snapshot.status.remainingReconnectAttempts,
      lastRecoveryDurationMs: recovery?.p95 ?? null,
      avgRecoveryDurationMs: recovery?.avg ?? null,
      p95RecoveryDurationMs: recovery?.p95 ?? null,
    },
    buffering: {
      supported: snapshot.capabilities.buffering,
      isBufferingAudio: snapshot.status.isBufferingAudio,
      isDrainingAudio: snapshot.status.isDrainingAudio,
      pendingBufferedAudioBytes: snapshot.status.pendingBufferedAudioBytes,
      persistedBufferedAudioBytes: snapshot.status.persistedBufferedAudioBytes,
      persistedBufferedAudioSegments: snapshot.status.persistedBufferedAudioSegments,
      totalBufferedAudioBytes: snapshot.status.totalBufferedAudioBytes,
      peakBufferedAudioBytes: getGauge(snapshot, "recovery.backlog.bytes") ?? 0,
      bufferingDurationMs: bufferingDuration,
      drainingDurationMs: drainingDuration,
      lastDrainProgressAt: snapshot.status.lastDrainProgressAt,
      drainCycles: snapshot.status.drainCycles,
      drainExitReason: snapshot.status.drainExitReason,
      finishDeliveryState: snapshot.status.finishDeliveryState,
      avgBufferGrowthRateBytesPerSecond: null,
      avgBufferDrainRateBytesPerSecond: null,
      bufferStallCount: getCounter(snapshot, "buffer.stall.count"),
    },
    browserNetwork: {
      supported: snapshot.capabilities.browserNetwork,
      onlineTransitionCount: onlineCount,
      offlineTransitionCount: offlineCount,
      offlineDurationMs: offlineDuration,
      flapCount,
      effectiveType: snapshot.status.networkEffectiveType,
      downlinkMbps: getGauge(snapshot, "network.downlink_mbps"),
      rttMs: getGauge(snapshot, "network.rtt_ms"),
    },
    audioCapture: {
      supported: snapshot.capabilities.audioCapture,
      inputSampleRate: getGauge(snapshot, "audio.capture.input_sample_rate"),
      targetSampleRate: getGauge(snapshot, "audio.capture.target_sample_rate"),
      channelCount: getGauge(snapshot, "audio.capture.channel_count"),
      currentRms: getGauge(snapshot, "audio.current_rms"),
      peakRms: getGauge(snapshot, "audio.peak_rms"),
      currentPeakAmplitude: getGauge(snapshot, "audio.current_peak_amplitude"),
      clippingEventCount: getCounter(snapshot, "audio.capture.clipping_events"),
      silenceRatio: getGauge(snapshot, "audio.capture.silence_ratio"),
      speechActivityRatio: getGauge(snapshot, "audio.capture.speech_activity_ratio"),
      audioCallbackGapCount: getCounter(snapshot, "audio.capture.gap.count"),
      longestAudioCallbackGapMs: getGauge(snapshot, "audio.capture.gap.longest_ms"),
    },
  };
};
